import React from 'react';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Briefcase, CheckCircle, XCircle, Eye, Edit, ExternalLink } from 'lucide-react';

/**
 * Admin → Teachers section.
 * Lists all teachers with status toggle + readiness checklist + stats.
 * All data + handlers are passed via props (pure presentational).
 */
export const TeachersSection = ({
  teachers,
  teacherTimeFilter,
  setTeacherTimeFilter,
  setAddTeacherOpen,
  getTeacherStats,
  teacherReadinessMap,
  fetchTeacherReadiness,
  handleSetTeacherActive,
  handleImpersonate,
  setEditTeacher,
  setViewTeacherProfile,
}) => (
  <>
    <div className="flex items-center justify-between mb-8">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Teachers</h1>
        <p className="text-slate-500 font-semibold">{teachers.length} registered teachers</p>
      </div>
      <div className="flex items-center gap-3">
        <Button className="bg-blue-600 hover:bg-blue-700 text-white font-bold" onClick={() => setAddTeacherOpen(true)} data-testid="add-teacher-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Teacher
        </Button>
        <span className="text-sm text-slate-500 font-semibold">View:</span>
        <Select value={teacherTimeFilter} onValueChange={setTeacherTimeFilter}>
          <SelectTrigger className="w-32 border-2 border-blue-400/30">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="day">Today</SelectItem>
            <SelectItem value="week">This Week</SelectItem>
            <SelectItem value="month">This Month</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>

    <div className="nb-card overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-blue-500/10">
            <TableHead className="font-semibold text-blue-700 font-bold">TID</TableHead>
            <TableHead className="font-semibold text-blue-700 font-bold">Teacher</TableHead>
            <TableHead className="font-semibold text-blue-700 font-bold text-center">Status</TableHead>
            <TableHead className="font-semibold text-blue-700 font-bold">Courses</TableHead>
            <TableHead className="font-semibold text-blue-700 font-bold">Students</TableHead>
            <TableHead className="font-semibold text-blue-700 font-bold text-center">Classes</TableHead>
            <TableHead className="font-semibold text-blue-700 font-bold text-center"><span className="text-emerald-600">Present</span></TableHead>
            <TableHead className="font-semibold text-blue-700 font-bold text-center"><span className="text-red-600">Absent</span></TableHead>
            <TableHead className="font-semibold text-blue-700 font-bold text-center"><span className="text-amber-600">Incomplete</span></TableHead>
            <TableHead className="font-semibold text-blue-700 font-bold text-center">Actions</TableHead>
            <TableHead className="font-semibold text-blue-700 font-bold text-center">Edit</TableHead>
            <TableHead className="font-semibold text-blue-700 font-bold text-center">Profile</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {teachers.map((teacher) => {
            const stats = getTeacherStats(teacher, teacherTimeFilter);
            const readiness = teacherReadinessMap[teacher.id];
            const isActive = readiness?.is_active_teaching ?? teacher.is_active_teaching;
            return (
              <TableRow key={teacher.id} className="hover:bg-slate-500/10">
                <TableCell>
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded font-mono text-xs font-bold">{teacher.teacher_id || '—'}</span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <Briefcase className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-900">{teacher.name}</p>
                      <p className="text-sm text-slate-500 font-semibold">{teacher.email}</p>
                      {teacher.phone && <p className="text-xs text-emerald-600 font-bold">{teacher.phone}</p>}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex flex-col items-center gap-1">
                    <button
                      onClick={async () => {
                        if (!readiness) await fetchTeacherReadiness(teacher.id);
                        handleSetTeacherActive(teacher.id, !isActive);
                      }}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border-2 ${isActive ? 'bg-emerald-100 text-emerald-700 border-emerald-600' : 'bg-slate-100 text-slate-600 border-slate-400'}`}
                      data-testid={`teacher-active-toggle-${teacher.id}`}
                      title={isActive ? 'Click to deactivate (off live classes)' : 'Click to activate for live classes'}
                    >
                      {isActive ? (<><CheckCircle className="w-3 h-3" /> Live</>) : (<><XCircle className="w-3 h-3" /> Off</>)}
                    </button>
                    <button
                      onClick={() => fetchTeacherReadiness(teacher.id)}
                      className="text-[10px] text-blue-600 hover:underline"
                      data-testid={`teacher-readiness-btn-${teacher.id}`}
                    >
                      {readiness ? 'refresh' : 'check readiness'}
                    </button>
                    {readiness && (
                      <div className="text-[9px] text-left bg-white border border-slate-200 rounded p-1.5 mt-1">
                        <div className={readiness.demo_completed ? 'text-emerald-600' : 'text-slate-400'}>● Demo {readiness.demo_completed ? '✓' : '–'}</div>
                        <div className={readiness.onboarding_complete ? 'text-emerald-600' : 'text-slate-400'}>● Onboard {readiness.onboarding_complete ? '✓' : '–'}</div>
                        <div className={readiness.availability_set ? 'text-emerald-600' : 'text-slate-400'}>● Avail {readiness.availability_set ? '✓' : '–'}</div>
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-center font-medium">{teacher.courses_count || 0}</TableCell>
                <TableCell className="text-center font-medium">{teacher.students_count || 0}</TableCell>
                <TableCell className="text-center font-bold text-blue-600">{stats.classes}</TableCell>
                <TableCell className="text-center">
                  <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full font-medium">{stats.present}</span>
                </TableCell>
                <TableCell className="text-center">
                  <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full font-medium">{stats.absent}</span>
                </TableCell>
                <TableCell className="text-center">
                  <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full font-medium">{stats.incomplete}</span>
                </TableCell>
                <TableCell className="text-center">
                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-slate-900 font-bold h-7 px-2.5 rounded-md"
                    onClick={() => handleImpersonate(teacher.id, teacher.name, 'teacher')}
                    data-testid={`impersonate-teacher-${teacher.id}`}
                    title="View as this teacher"
                  >
                    <Eye className="w-3.5 h-3.5 mr-1" /><span className="text-xs">Impersonate</span>
                  </Button>
                </TableCell>
                <TableCell className="text-center">
                  <Button size="sm" variant="outline"
                    className="font-bold h-7 px-2.5 rounded-md border-amber-400 text-amber-700 hover:bg-amber-50"
                    onClick={() => setEditTeacher({ ...teacher })}
                    data-testid={`edit-teacher-${teacher.id}`}>
                    <Edit className="w-3.5 h-3.5 mr-1" /><span className="text-xs">Edit</span>
                  </Button>
                </TableCell>
                <TableCell className="text-center">
                  <Button
                    size="sm"
                    variant="outline"
                    className="font-bold h-7 px-2.5 rounded-md border-[#3B82F6] text-blue-700 font-bold hover:bg-[#3B82F6]/10"
                    onClick={() => setViewTeacherProfile(teacher)}
                    data-testid={`view-profile-${teacher.id}`}
                  >
                    <ExternalLink className="w-3.5 h-3.5 mr-1" /><span className="text-xs">View</span>
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  </>
);

export default TeachersSection;
